import { CustomerFormData, AIQualificationResult } from '../components/CustomerForm';

export interface LeadApiResponse {
  success: boolean;
  lead_id: string;
  message: string;
  qualification?: AIQualificationResult;
}

class LeadService {
  private baseUrl = '/api';

  /**
   * Submit customer form and create lead
   */
  async submitLead(formData: CustomerFormData): Promise<LeadApiResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));

        // Handle structured error response from backend
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (typeof errorData.detail === 'object') {
            // Backend sends structured error: { error: "...", message: "...", details: {...} }
            errorMessage = errorData.detail.message || errorData.detail.error || JSON.stringify(errorData.detail);
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }

        throw new Error(errorMessage);
      }

      // Backend returns: { lead_id, status, timestamp }
      // Convert to expected format: { success, lead_id, message }
      const backendResponse = await response.json();
      return {
        success: backendResponse.status === 'created',
        lead_id: backendResponse.lead_id,
        message: 'Lead erfolgreich erstellt'
      };
    } catch (error) {
      console.error('Lead submission failed:', error);
      throw new Error(
        error instanceof Error
          ? error.message
          : 'Fehler beim Senden der Anfrage. Bitte versuchen Sie es später erneut.'
      );
    }
  }

  /**
   * Get AI qualification for potential lead using real AI profiling
   */
  async getAIQualification(
    formData: Partial<CustomerFormData>,
    analysisResults?: any
  ): Promise<AIQualificationResult> {
    try {
      // Call real AI profiling endpoint
      const response = await fetch(`${this.baseUrl}/leads/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form_data: formData,
          analysis_results: analysisResults
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.profile) {
          // Convert AI profile to qualification result
          return this.convertProfileToQualification(data.profile, data.communication_template);
        }
      }

      // Fallback to client-side calculation
      console.log('AI API not available, using client-side fallback');
      return this.calculateAIQualificationClientSide(formData, analysisResults);

    } catch (error) {
      console.error('AI qualification failed, using fallback:', error);
      return this.calculateAIQualificationClientSide(formData, analysisResults);
    }
  }

  /**
   * Convert AI profile response to qualification result
   */
  private convertProfileToQualification(profile: any, template: any): AIQualificationResult {
    // Convert technical level to business qualification
    const levelToCategory: Record<string, AIQualificationResult['category']> = {
      'technician': 'high_potential',
      'electrician': 'medium_potential',
      'facility': 'medium_potential',
      'business': 'high_potential',
      'consumer': 'low_potential'
    };

    const levelToPriority: Record<string, AIQualificationResult['priority']> = {
      'technician': 'high',
      'electrician': 'medium',
      'facility': 'medium',
      'business': 'urgent',
      'consumer': 'low'
    };

    const category = levelToCategory[profile.technical_level] || 'medium_potential';
    const priority = levelToPriority[profile.technical_level] || 'medium';

    // Calculate score based on confidence and level
    let score = Math.round(profile.confidence * 100);
    if (profile.technical_level === 'business' || profile.technical_level === 'technician') {
      score = Math.min(100, score + 20); // Boost for high-value contacts
    }

    // Enhanced reasoning with AI insights
    const reasoning = [
      `AI-Analyse: ${profile.technical_level} (${(profile.confidence * 100).toFixed(0)}% Konfidenz)`,
      ...profile.reasoning,
      `Empfohlener Kommunikationsstil: ${template.tone}`,
      `Antwortzeit-Erwartung: ${template.response_time_expectation}`
    ];

    // Enhanced recommendations with communication insights
    const recommendations = [
      profile.recommended_approach,
      `Prioritäre Themen: ${profile.priority_topics.slice(0, 3).join(', ')}`,
      `Vermeiden: ${profile.avoid_topics.slice(0, 2).join(', ')}`,
      `Anlagen bereitstellen: ${template.preferred_attachments.slice(0, 2).join(', ')}`
    ];

    return {
      score,
      category,
      reasoning,
      recommendations,
      priority
    };
  }

  /**
   * Client-side AI qualification calculation
   * This is a simplified version that could be enhanced with real AI
   */
  private calculateAIQualificationClientSide(
    formData: Partial<CustomerFormData>,
    analysisResults?: any
  ): AIQualificationResult {
    let score = 0;
    const reasoning: string[] = [];
    const recommendations: string[] = [];

    // Company evaluation
    if (formData.company_name && formData.company_name.length > 5) {
      score += 15;
      reasoning.push("Etabliertes Unternehmen mit aussagekräftigem Namen");
    }

    // Industry-based evaluation
    const highValueIndustries = ['Manufacturing', 'Automotive', 'Energy', 'Healthcare', 'Industrial'];
    if (formData.industry && highValueIndustries.includes(formData.industry)) {
      score += 25;
      reasoning.push(`${formData.industry}: Branche mit hohem Hybrid-Speicher-Potenzial`);
    }

    // Customer type evaluation
    if (formData.customer_type === 'B2B') {
      score += 20;
      reasoning.push("B2B-Kunde: Höhere Wahrscheinlichkeit für größere Projekte");
    }

    // Analysis result-based evaluation
    if (analysisResults?.score) {
      const scoreLevel = typeof analysisResults.score.level === 'string'
        ? analysisResults.score.level
        : analysisResults.score.level?.name;

      if (scoreLevel === 'GREEN' || scoreLevel === 'green') {
        score += 30;
        reasoning.push("Analyseergebnisse zeigen optimale Eignung für Hybrid-Speicher");
        recommendations.push("Sofortige Kontaktaufnahme empfohlen - Idealer Kunde");
      } else if (scoreLevel === 'YELLOW' || scoreLevel === 'yellow') {
        score += 15;
        reasoning.push("Analyseergebnisse zeigen Potenzial für Hybrid-Speicher");
        recommendations.push("Detailberatung anbieten - Gutes Potenzial");
      } else {
        score += 5;
        reasoning.push("Analyseergebnisse zeigen begrenzte Eignung");
        recommendations.push("Aufklärung über Optimierungsmöglichkeiten");
      }
    }

    // Quantity category evaluation
    const quantities: Record<string, number> = {
      'Großserie': 25,
      'Kleinserie': 15,
      'Prototyp': 10,
      'Einzelstück': 5
    };
    const quantityScore = quantities[formData.quantity_category || ''] || 0;
    score += quantityScore;

    if (quantityScore > 15) {
      reasoning.push("Große Abnahmemenge indiziert");
    }

    // Determine category and priority
    let category: AIQualificationResult['category'];
    let priority: AIQualificationResult['priority'];

    if (score >= 80) {
      category = 'high_potential';
      priority = 'urgent';
      recommendations.push("Sofortige Bearbeitung innerhalb 2 Stunden");
    } else if (score >= 60) {
      category = 'medium_potential';
      priority = 'high';
      recommendations.push("Bearbeitung innerhalb 24 Stunden");
    } else if (score >= 40) {
      category = 'low_potential';
      priority = 'medium';
      recommendations.push("Bearbeitung innerhalb 3 Tagen");
    } else {
      category = 'not_qualified';
      priority = 'low';
      recommendations.push("Standard-Bearbeitung, Potenzial prüfen");
    }

    return {
      score: Math.min(100, score),
      category,
      reasoning,
      recommendations,
      priority
    };
  }

  /**
   * Get leads list (admin function)
   */
  async getLeads(filters?: any): Promise<any[]> {
    try {
      const queryParams = filters ? `?${new URLSearchParams(filters)}` : '';
      const response = await fetch(`${this.baseUrl}/leads${queryParams}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      throw error;
    }
  }

  /**
   * Send test email (for testing email integration)
   */
  async sendTestEmail(email: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/leads/test-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      return response.ok;
    } catch (error) {
      console.error('Test email failed:', error);
      return false;
    }
  }
}

export const leadService = new LeadService();